// ABOUTME: Utility functions for the stage pill and heart backdrop on the journey timeline avatar.
// ABOUTME: Handles pill text color, stage names, heart backdrop visibility, and pill positioning.

import { ponoMeterLadderColors, pillContrastText } from '../theme/tokens';

/** Ladder position colors matching PonoMeter colors from child dashboard */
export const LADDER_POSITION_COLORS: Record<number, string> = ponoMeterLadderColors;

/**
 * Stage names for the pill display. Stage 2 uses a line break for the long label.
 * Line-break placement differs from LADDER_POSITIONS in AnalyticsTimeChart because
 * the pill has different width constraints than the Y-axis labels.
 */
export const PILL_STAGE_NAMES: Record<number, string> = {
  1: 'Stop & Reset',
  2: 'Breathe &\nTry Again',
  3: 'Pause',
  4: 'Ready',
  5: 'Good Job',
  6: 'Great Day',
  7: 'Earning a Heart',
};

/**
 * Returns the text color for a pill based on the ladder position.
 * Light backgrounds (orange stage 3, amber stage 4, lime stage 5, light green stage 6)
 * get dark text; the darker stages (red 1, deep orange 2, green 7) get white text.
 * Meets WCAG AA contrast ratio (4.5:1 minimum).
 */
export function getPillTextColor(ladderPosition: number): string {
  if (ladderPosition >= 3 && ladderPosition <= 6) {
    return pillContrastText.dark;
  }
  return pillContrastText.light;
}

/**
 * Returns the stage name to display in the pill.
 * Falls back to "Stage N" for unknown positions.
 */
export function getPillStageName(ladderPosition: number): string {
  return PILL_STAGE_NAMES[ladderPosition] || `Stage ${ladderPosition}`;
}

/**
 * Determines whether the heart backdrop should be shown behind the avatar.
 * - Completed past days: always show
 * - Today: only when ladderPosition >= 6 (Great Day or Earning a Heart)
 * - Future dates: never show (positionType will be 'current-time' but hour <= 4 indicates future)
 */
export function shouldShowHeartBackdrop(
  positionType: 'current-time' | 'end-of-day',
  ladderPosition: number,
  isFutureDate: boolean
): boolean {
  if (isFutureDate) {
    return false;
  }
  if (positionType === 'end-of-day') {
    return true;
  }
  // Today: show heart when at heart-earning level
  return ladderPosition >= 6;
}

/**
 * Determines whether the heart behind the avatar is "earned" (filled green)
 * or "not earned" (grey outline).
 * - Completed days: use backend earnedHeart value
 * - Today: derive from ladderPosition >= 6
 */
export function getHeartEarnedState(
  positionType: 'current-time' | 'end-of-day',
  ladderPosition: number,
  earnedHeart?: boolean
): boolean {
  if (positionType === 'end-of-day' && earnedHeart !== undefined) {
    return earnedHeart;
  }
  return ladderPosition >= 6;
}

/**
 * Returns 'right' or 'left' indicating which side of the avatar the pill should appear on.
 * Flips to the left when the avatar is in the last ~20% of the chart's time axis (hour > 19.2).
 */
export function getPillSide(hour: number): 'right' | 'left' {
  if (hour > 19.2) {
    return 'left';
  }
  return 'right';
}
